import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  Avatar,
  Chip,
  Grid,
  Button,
  Divider,
  Stack,
  IconButton,
  Breadcrumbs,
  Link as MuiLink,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert
} from "@mui/material";
import {
  ArrowBack,
  Share,
  BookmarkBorder,
  Bookmark,
  Facebook,
  Twitter,
  LinkedIn,
  Email,
  AccessTime,
  CalendarToday,
  TrendingUp,
  Download
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import UserQuery from "../stateQueries/User";
import { format } from "date-fns";
import { v4 as uuidv4 } from "uuid";

const ArticleDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [browserId, setBrowserId] = useState(null);

  const queryClient = useQueryClient();

  useEffect(() => {
    let storedBrowserId = localStorage.getItem("browserId");
    if (!storedBrowserId) {
      storedBrowserId = uuidv4();
      localStorage.setItem("browserId", storedBrowserId);
    }
    setBrowserId(storedBrowserId);
  }, []);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["article", id, browserId],
    queryFn: async () => await UserQuery.getArticleById(id, browserId),
    enabled: !!id && !!browserId // Only run the query if id and browserId are available
  });

  console.log(data);

  const article = data?.article || {};

  // Safely parse a tag that may be a plain string or a JSON {value,label} object
  function parseTag(tag) {
    try { return JSON.parse(tag); } catch { return { value: tag, label: tag }; }
  }

  const handleBackToArticles = () => {
    navigate("/articles");
  };

  const handleBackToHome = () => {
    navigate("/home");
  };

  const toggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const handleShare = async (platform) => {
    const url = window.location.href;
    const text = article.title;

    try {
      await UserQuery.incrementArticleShares(id);
      queryClient.invalidateQueries(["article", id, browserId]);
    } catch (error) {
      console.error("Error incrementing shares:", error);
      // Optionally, show an error message to the user
    }

    switch (platform) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            url
          )}`
        );
        break;
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            url
          )}&text=${encodeURIComponent(text)}`
        );
        break;
      case "linkedin":
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            url
          )}`
        );
        break;
      case "email":
        window.open(
          `mailto:?subject=${encodeURIComponent(
            text
          )}&body=${encodeURIComponent(url)}`
        );
        break;
      default:
        break;
    }
    setShowShareMenu(false);
  };

  // const formatDate = (dateString) => {
  //   const options = { year: "numeric", month: "long", day: "numeric" };
  //   return new Date(dateString).toLocaleDateString("en-US", options);
  // };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress color="primary" size={60} />
        <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
          Loading article...
        </Typography>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          }
        >
          Error: {error.message}
        </Alert>
      </Container>
    );
  }

  if (!article || !article.id) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Article not found.
        </Typography>
        <Button variant="outlined" onClick={handleBackToArticles}>
          Back to Articles
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 4 }}>
        <MuiLink
          component="button"
          variant="body2"
          onClick={handleBackToHome}
          sx={{ cursor: "pointer", color: "primary.main" }}
        >
          Home
        </MuiLink>
        <MuiLink
          component="button"
          variant="body2"
          onClick={handleBackToArticles}
          sx={{ cursor: "pointer", color: "primary.main" }}
        >
          Articles
        </MuiLink>
        <Typography color="text.primary" variant="body2">
          {article.title}
        </Typography>
      </Breadcrumbs>

      {/* Back Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={handleBackToArticles}
        sx={{ mb: 3, color: "primary.main" }}
      >
        Back to Articles
      </Button>

      {/* Article Header */}
      <Box sx={{ mb: 6 }}>
        <Chip
          label={
            article?.tags && article?.tags?.length > 0
              ? parseTag(article?.tags[0]).label
              : ""
          }
          size="medium"
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            mb: 3,
            fontWeight: 500,
            fontSize: "0.9rem"
          }}
        />

        <Typography
          variant="h1"
          component="h1"
          fontWeight="bold"
          sx={{
            color: "text.primary",
            mb: 3,
            fontSize: { xs: "2rem", md: "2.5rem", lg: "3rem" },
            lineHeight: 1.2
          }}
        >
          {article?.title}
        </Typography>

        <Typography
          variant="h5"
          color="text.secondary"
          sx={{
            mb: 4,
            lineHeight: 1.6,
            fontWeight: 400
          }}
        >
          {article?.subTitle}
        </Typography>

        {/* Article Meta Information */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 3,
            mb: 4
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main" }}>
              {article?.author?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="600">
                {article?.author}
              </Typography>
              {/* <Typography variant="body2" color="text.secondary">
                {article.authorTitle}
              </Typography> */}
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarToday fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {format(new Date(article?.createdAt), "MMMM dd, yyyy")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTime fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {article?.readTimeValue} {article?.readTimeUnit}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TrendingUp fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {article?.articleViews?.length} views
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={isBookmarked ? <Bookmark /> : <BookmarkBorder />}
            onClick={toggleBookmark}
            sx={{ borderRadius: 2 }}
          >
            {isBookmarked ? "Bookmarked" : "Bookmark"}
          </Button>

          <Button
            variant="outlined"
            startIcon={<Share />}
            onClick={() => setShowShareMenu(true)}
            sx={{ borderRadius: 2 }}
          >
            Share
          </Button>

          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => {
              window.open(
                `${process.env.REACT_APP_API_URL}/api/dev/admin/article/downloadNewsLetter?filename=${article?.documentURL}`,
                "_blank"
              );
            }}
            sx={{ borderRadius: 2 }}
          >
            Download Newsletter
          </Button>
        </Box>

        {/* Share Menu */}
        {showShareMenu && (
          <Paper
            sx={{
              mt: 2,
              p: 2,
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              maxWidth: "fit-content"
            }}
          >
            <IconButton onClick={() => handleShare("facebook")} color="primary">
              <Facebook />
            </IconButton>
            <IconButton onClick={() => handleShare("twitter")} color="primary">
              <Twitter />
            </IconButton>
            <IconButton onClick={() => handleShare("linkedin")} color="primary">
              <LinkedIn />
            </IconButton>
            <IconButton onClick={() => handleShare("email")} color="primary">
              <Email />
            </IconButton>
          </Paper>
        )}
      </Box>

      {/* Featured Image */}
      <Box sx={{ mb: 6 }}>
        <img
          src={
            article?.imageURL?.startsWith("http")
              ? article.imageURL
              : `${process.env.REACT_APP_API_URL}/uploads/articles/${article?.imageURL}`
          }
          alt={article.title}
          style={{
            width: "100%",
            height: "auto",
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            objectFit: "cover"
          }}
        />
      </Box>

      {/* Article Content */}
      <Grid container spacing={6}>
        <Grid item xs={12} lg={8}>
          <Box
            sx={{
              "& h2": {
                fontSize: "1.75rem",
                fontWeight: "bold",
                color: "text.primary",
                mt: 4,
                mb: 2
              },
              "& h3": {
                fontSize: "1.5rem",
                fontWeight: "bold",
                color: "text.primary",
                mt: 3,
                mb: 2
              },
              "& p": {
                fontSize: "1.1rem",
                lineHeight: 1.8,
                color: "text.secondary",
                mb: 2
              },
              "& ul": {
                pl: 3,
                mb: 2
              },
              "& li": {
                fontSize: "1.1rem",
                lineHeight: 1.8,
                color: "text.secondary",
                mb: 1
              },
              "& strong": {
                fontWeight: "bold",
                color: "text.primary"
              }
            }}
            dangerouslySetInnerHTML={{ __html: article?.content }}
          />
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={4}>
            {/* Tags */}
            <Card sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Tags
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {article?.tags?.map((tag, index) => {
                  const parsedTag = JSON.parse(tag);
                  return (
                    <Chip
                      key={index}
                      label={parsedTag.label}
                      size="small"
                      sx={{
                        backgroundColor: "primary.light",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "primary.main"
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Card>

            {/* Article Stats */}
            <Card sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Article Stats
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <AccessTime color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Read Time"
                    secondary={`${article?.readTimeValue} ${article?.readTimeUnit}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TrendingUp color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Views"
                    secondary={String(article?.articleViews?.length || 0)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Share color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Shares"
                    secondary={String(article?.shares || 0)}
                  />
                </ListItem>
              </List>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Divider */}
      <Divider sx={{ my: 6 }} />

      {/* Call to Action */}
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
          Stay Updated with Our Latest Articles
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          Get insights delivered to your inbox and never miss the latest
          developments in the financial services sector.
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          flexWrap="wrap"
        >
          <Button
            variant="contained"
            size="large"
            onClick={handleBackToArticles}
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontSize: "1.1rem",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "primary.dark",
                transform: "translateY(-2px)",
                boxShadow: "0 12px 35px rgba(0,0,0,0.2)"
              }
            }}
          >
            Browse All Articles
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={handleBackToHome}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontSize: "1.1rem",
              fontWeight: 600,
              textTransform: "none",
              borderWidth: 2
            }}
          >
            Back to Home
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};

export default ArticleDetail;
